/**
 * Decides whether a pro-initiated (consultation-linked) assisted launch must
 * reset the device's persisted session before proceeding.
 *
 * A pro-initiated launch must always start a fresh session on a shared in-office
 * device — it should resume ONLY when re-entering the exact same launch (same
 * questionnaire AND same patient). Anything else (different patient, different
 * questionnaire, or a prior in-progress session that has no questionnaire id
 * yet) is a stale session that must be reset, otherwise its answers/graph state
 * would bleed into the newly linked consultation.
 *
 * Note: a consultation-linked launch always carries a `launchQuestionnaireId`
 * (the launch endpoint creates the questionnaire before the patient navigates),
 * so a legitimate resume of the same launch always matches and is never reset.
 * The `existingQuestionnaireId`-absent case only arises from a prior generic
 * free-start (which persists its questionnaire id on submit), which is exactly
 * the stale state we want to clear.
 */
export const shouldResetAssistedLaunch = ({
  existingQuestionnaireId,
  existingPatientId,
  launchQuestionnaireId,
  launchPatientId,
}: {
  existingQuestionnaireId: string | null | undefined;
  existingPatientId: string | null | undefined;
  launchQuestionnaireId: string | null | undefined;
  launchPatientId: string;
}): boolean => {
  const hasPriorSession = !!existingPatientId || !!existingQuestionnaireId;
  if (!hasPriorSession) return false;

  const isResumingSameLaunch =
    !!existingQuestionnaireId &&
    existingQuestionnaireId === launchQuestionnaireId &&
    existingPatientId === launchPatientId;

  return !isResumingSameLaunch;
};
